import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

// Change WHATSAPP_NUMBER and MESSAGE to your real values.
const WHATSAPP_NUMBER = "+1 (408) 438-5390";
const WHATSAPP_NUMBER_RAW = "14084385390"; // country code + number, digits only
const DEFAULT_MESSAGE =
  "Hi Dr. Santosh / Dr. Navita, I saw the Shemushi Inner Compass site and would like to know more about enrollment.";

function WhatsAppIcon({ size = 22, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <path
        d="M16 3C8.82 3 3 8.82 3 16c0 2.29.6 4.5 1.75 6.44L3 29l6.72-1.76A12.94 12.94 0 0016 29c7.18 0 13-5.82 13-13S23.18 3 16 3zm0 23.68c-2.05 0-4.06-.56-5.8-1.62l-.42-.25-3.99 1.04 1.07-3.88-.27-.44A10.62 10.62 0 015.31 16C5.31 10.1 10.1 5.31 16 5.31S26.69 10.1 26.69 16 21.9 26.68 16 26.68zm6.15-7.98c-.34-.17-2.02-1-2.33-1.11-.31-.11-.54-.17-.77.17-.23.34-.88 1.11-1.08 1.34-.2.23-.4.26-.74.09-.34-.17-1.44-.53-2.75-1.7-1.02-.9-1.7-2.02-1.9-2.36-.2-.34-.02-.53.15-.7.15-.15.34-.4.51-.6.17-.2.23-.34.34-.57.11-.23.06-.43-.03-.6-.09-.17-.77-1.86-1.06-2.55-.28-.67-.56-.58-.77-.59l-.65-.01c-.23 0-.6.08-.91.4-.31.31-1.2 1.17-1.2 2.85 0 1.68 1.23 3.3 1.4 3.53.17.23 2.42 3.7 5.87 5.19.82.35 1.46.56 1.96.72.82.26 1.57.22 2.16.14.66-.1 2.02-.83 2.31-1.63.28-.8.28-1.48.2-1.63-.09-.15-.31-.23-.65-.4z"
        fill={color}
      />
    </svg>
  );
}

export default function WhatsAppButton() {
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setPulse(true), 1800);
    return () => clearTimeout(t);
  }, []);

  const chatUrl = `https://wa.me/${WHATSAPP_NUMBER_RAW}?text=${encodeURIComponent(DEFAULT_MESSAGE)}`;

  return (
    <>
      {/* Floating button */}
      <div className="fixed bottom-5 right-5 md:bottom-8 md:right-8 z-[70]" data-testid="whatsapp-fab-wrapper">
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: 12, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 12, scale: 0.94 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="mb-3 w-[300px] md:w-[340px] border-2 overflow-hidden shadow-[8px_8px_0_0_var(--ink)]"
              style={{ borderColor: "var(--ink)", background: "var(--bg)" }}
              data-testid="whatsapp-panel"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b-2" style={{ borderColor: "var(--ink)", background: "#25D366" }}>
                <div className="flex items-center gap-2 text-white">
                  <WhatsAppIcon size={20} />
                  <span className="font-mono-editorial text-[10px] tracking-[0.28em]">WHATSAPP</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close WhatsApp panel"
                  className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/30 transition"
                  data-testid="whatsapp-close-btn"
                >
                  <X size={14} />
                </button>
              </div>

              {/* Body */}
              <div className="p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center font-display italic text-lg" style={{ borderColor: "var(--ink)", background: "var(--sunflower)" }}>
                    S
                  </div>
                  <div>
                    <div className="font-display text-lg leading-none text-[color:var(--ink)]">
                      Dr. Santosh / Dr. Navita
                    </div>
                    <div className="font-mono-editorial text-[10px] tracking-[0.22em] text-[color:var(--muted)] mt-1">
                      REPLIES WITHIN A FEW HOURS
                    </div>
                  </div>
                </div>

                <div
                  className="mt-4 p-3 rounded-lg text-sm leading-snug text-[color:var(--ink)]/85"
                  style={{ background: "var(--bg-2)" }}
                >
                  Namaste! We&rsquo;re the founders of Shemushi. Ask us anything about Inner Compass — enrollment, batch fit, or the pre-assessment.
                </div>

                <div className="mt-4 text-[11px] font-mono-editorial tracking-[0.18em] text-[color:var(--muted)]">
                  {WHATSAPP_NUMBER}
                </div>

                <a
                  href={chatUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex w-full items-center justify-between h-11 px-4 rounded-full text-white transition-transform hover:-translate-y-0.5"
                  style={{ background: "#25D366" }}
                  data-testid="whatsapp-start-chat"
                >
                  <span className="flex items-center gap-2 text-sm font-medium">
                    <WhatsAppIcon size={16} /> Start chat on WhatsApp
                  </span>
                  <span>→</span>
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="relative">
          {/* Pulse ring */}
          {pulse && !open && (
            <motion.span
              className="absolute inset-0 rounded-full pointer-events-none"
              style={{ background: "#25D366" }}
              animate={{ scale: [1, 1.55], opacity: [0.55, 0] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeOut" }}
              aria-hidden
            />
          )}
          <button
            onClick={() => setOpen((v) => !v)}
            className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center shadow-[4px_4px_0_0_var(--ink)] border-2 transition-transform hover:-translate-y-0.5 active:translate-y-0"
            style={{ background: "#25D366", borderColor: "var(--ink)" }}
            aria-label={open ? "Close WhatsApp chat" : "Open WhatsApp chat"}
            aria-expanded={open}
            data-testid="whatsapp-fab"
          >
            <WhatsAppIcon size={26} />
          </button>
        </div>
      </div>
    </>
  );
}
